import { Menu, Bell } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { cn } from '@utils/cn';
import { toggleMobileMenu } from '@store/slices/uiSlice';
import { fetchDashboardOverview } from '@store/slices/dashboardSlice';
import { fetchNotifications } from '@store/slices/notificationSlice';
import { APP_ROUTES } from '@config/routes';
import { BROWSER_NOTIFICATION_EVENT } from '@services/browserPushService';

type UserProfile = {
  displayName?: string | null;
  role?: string | null;
  photoURL?: string | null;
};

// Map routes to page titles
const getPageTitle = (pathname: string): string => {
  if (pathname === APP_ROUTES.DASHBOARD) return 'Dashboard';

  if (pathname === APP_ROUTES.PROJECTS) return 'Projects';
  if (pathname === APP_ROUTES.EMAIL) return 'Messages';
  if (pathname === APP_ROUTES.NOTIFICATIONS) return 'Notifications';
  if (pathname === APP_ROUTES.SUPPORT) return 'Help & Support';
  if (pathname === APP_ROUTES.WORKFLOW) return 'Workflow';
  if (pathname === APP_ROUTES.CLIENTS) return 'Clients';
  if (pathname === APP_ROUTES.CALENDAR) return 'Upcoming Events';
  if (pathname === APP_ROUTES.TEMPLATES) return 'Templates';
  if (pathname === APP_ROUTES.SETTINGS) return 'Settings';
  if (pathname === APP_ROUTES.BILLING) return 'Billing';
  if (pathname === APP_ROUTES.WIDGET_SETTINGS) return 'Widget Settings';
  if (pathname === APP_ROUTES.ACCOUNT_SETTINGS) return 'Account Settings';
  if (pathname === APP_ROUTES.HELP) return 'Help & Support';
  if (pathname === APP_ROUTES.CHANGELOG) return 'Changelog';
  return 'Dashboard';
};

// Map routes to page descriptions
const getPageDescription = (pathname: string, userProfile: UserProfile | null): string => {
  if (pathname === APP_ROUTES.DASHBOARD)
    return `Hello, ${userProfile?.displayName || 'User'}! Here's what's happening with your AI assistant today.`;

  if (pathname === APP_ROUTES.PROJECTS)
    return 'Manage your leads and jobs from inquiry to completion.';
  if (pathname === APP_ROUTES.EMAIL)
    return 'Manage synced emails, drafts, and follow-ups from one inbox.';
  if (pathname === APP_ROUTES.NOTIFICATIONS)
    return 'Track approvals, replies, bookings, and payment alerts from the same feed used on mobile.';
  if (pathname === APP_ROUTES.SUPPORT)
    return 'Browse help articles, troubleshoot issues, and open support tickets from one place.';
  if (pathname === APP_ROUTES.WORKFLOW)
    return 'Manage workflows across client projects and review the project workflow default copy for each client.';
  if (pathname === APP_ROUTES.CLIENTS)
    return 'Track each client account with linked contacts, projects, and event timeline.';
  if (pathname === APP_ROUTES.CALENDAR)
    return 'Manage your scheduled appointments and meetings created via the AI chat widget.';
  if (pathname === APP_ROUTES.TEMPLATES)
    return 'Create and manage reusable quote, invoice, contract, questionnaire, and email templates for your projects.';
  if (pathname === APP_ROUTES.WORKFLOW)
    return 'Build and review per-project workflows. Each project starts from the default workflow and can be customized independently.';
  if (pathname === APP_ROUTES.SETTINGS)
    return 'Configure your business, billing, templates, and workspace settings.';
  if (pathname === APP_ROUTES.BILLING) return 'Manage your subscription and billing information.';
  if (pathname === APP_ROUTES.WIDGET_SETTINGS)
    return 'Customize your AI assistant widget appearance and behavior.';
  if (pathname === APP_ROUTES.ACCOUNT_SETTINGS)
    return 'Update your profile, preferences, and account settings.';
  if (pathname === APP_ROUTES.HELP) return 'Find answers to common questions and get support.';
  if (pathname === APP_ROUTES.CHANGELOG)
    return 'Stay up to date with the latest features, improvements, and fixes.';
  return '';
};

const DashboardHeader = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { userProfile } = useAppSelector(state => state.auth);
  const notifications = useAppSelector(state => state.notifications.items);
  const pageTitle = getPageTitle(location.pathname);
  const pageDescription = getPageDescription(location.pathname, userProfile);

  const refreshNotifications = useCallback(
    (force = false) => {
      dispatch(fetchNotifications({ limit: 100, force }));
    },
    [dispatch]
  );

  const activeNotifications = notifications.filter(notification => {
    return notification.status !== 'dismissed' && notification.status !== 'snoozed';
  });

  const notificationCount = activeNotifications.filter(notification => {
    return notification.status === 'unread';
  }).length;

  // Fetch dashboard overview on mount and poll every 3 minutes.
  // DashboardHeader is the single owner of this fetch — it is always rendered
  // in the layout, so no other page needs to dispatch fetchDashboardOverview on mount.
  useEffect(() => {
    dispatch(fetchDashboardOverview(undefined));
    refreshNotifications();

    const interval = setInterval(() => {
      if (!document.hidden) {
        dispatch(fetchDashboardOverview(undefined));
        refreshNotifications();
      }
    }, 180000); // 3 minutes

    return () => clearInterval(interval);
  }, [dispatch, refreshNotifications]);

  useEffect(() => {
    // On tab re-focus, let the Redux cache decide whether a refetch is needed
    // (within the 30s window it will use cached data; outside it will refetch).
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        dispatch(fetchDashboardOverview(undefined));
        refreshNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [dispatch, refreshNotifications]);

  useEffect(() => {
    const handleNotificationReceived = () => {
      refreshNotifications(true);
      dispatch(fetchDashboardOverview(undefined));
    };

    window.addEventListener(BROWSER_NOTIFICATION_EVENT, handleNotificationReceived);
    return () => {
      window.removeEventListener(BROWSER_NOTIFICATION_EVENT, handleNotificationReceived);
    };
  }, [dispatch, refreshNotifications]);

  return (
    <header className="sticky top-0 z-40 font-sans">
      <div className={cn('flex items-center justify-between py-4 px-8 transition-colors bg-white')}>
        <div className="flex items-center gap-6">
          {/* Hamburger Menu - Mobile Only */}
          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => dispatch(toggleMobileMenu())}
            className="p-2 rounded-lg lg:hidden hover:bg-primary-150"
          >
            <Menu size={20} />
          </button>

          {/* Page Title */}
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">{pageTitle}</h1>
            <p className="text-base text-gray-500">{pageDescription}</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <button
            type="button"
            aria-label="Open notifications"
            onClick={() => navigate(APP_ROUTES.NOTIFICATIONS)}
            className="relative p-2 rounded-lg hover:bg-gray-100"
          >
            <Bell size={20} />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[10px] leading-[18px] text-center font-semibold tabular-nums whitespace-nowrap">
                {notificationCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
